import { apiFetch } from '@/lib/api-client';
import type { LogAddress } from '@uniticket/shared';

// TEMPORARY debug tool — "Action log + reverse" (see docs/PRODUCTION-READINESS.md §7). Admin-only.
// The daily-throughput block that used to live here moved to the Statistics page (useValidationStats).

export interface ActionLogCounts { sql: number; api: number; mutations: number; external: number; errors: number; }

export interface ActionLogItem {
  id: string;
  createdAt: string;
  userName: string | null;
  userEmail: string | null;
  method: string;
  path: string;
  action: string;
  summary: string | null;
  ticketId: string | null;
  /** UT-… resolved server-side from ticketId — what makes the row a readable way back to the ticket
   *  (Anouk 2026-08-10). null when the ticket is unknown; the link then falls back to the uuid. */
  ticketNumber: string | null;
  statusCode: number | null;
  reverseState: string;
  counts: ActionLogCounts;
  /** Sur QUELLE adresse l'action a porté (demande user 2026-10-06) — reconstituée par le serveur depuis les
   *  images de l'écriture ou la fiche du ticket. null quand l'action ne concerne aucune adresse (réglage,
   *  utilisateur…) ; `source` null quand on ne connaît que le JVID. */
  address: LogAddress | null;
  /** Les AUTRES adresses touchées par la même action (ajout de plusieurs boîtes, échange, lot…). */
  otherAddresses: number;
  // Calculés par le serveur depuis les images déjà stockées — donc valables sur tout l'historique.
  /** Nombre de champs réellement modifiés (≠ nombre de mutations). */
  changedFields?: number;
  /** Le premier changement, en une phrase : « delivery_status 7013 → 7015 ». */
  headline?: string | null;
  tables?: string[];
  /** L'action, lisible : jamais « unknown » (voir actionLabelFor). */
  label?: string;
  /** Bruit d'historique (prise de verrou) — masquable d'un clic. */
  noise?: boolean;
}

export interface SqlEntry { source: string; text: string; params?: string; durationMs?: number; }
export interface ApiEntry { method: string; url: string; status?: number; durationMs?: number; error?: string; }
export interface MutationEntry { source: string; table: string; op: string; key: unknown; before: unknown; after: unknown; reversible: boolean; }
export interface ExternalEffect { type: string; detail: string; }
export interface ErrorEntry { source: string; operation: string; detail: string; code?: string; at?: number; }

export interface ActionLogDetail {
  id: string;
  createdAt: string;
  userName: string | null;
  userEmail: string | null;
  method: string;
  path: string;
  action: string;
  summary: string | null;
  ticketId: string | null;
  statusCode: number | null;
  reverseState: string;
  reversedAt: string | null;
  reverseNote: string | null;
  sql: SqlEntry[];
  apiCalls: ApiEntry[];
  mutations: MutationEntry[];
  externalEffects: ExternalEffect[];
  errors: ErrorEntry[];
}

export interface ReverseResult { state: string; reversed: number; skipped: number; note: string; }

/** Un choix d'une liste de filtre, avec son volume dans le périmètre courant (recherche + dates). */
export interface ActionLogFacet { value: string; label: string; count: number }

/** Paged listing. `meta.total` is the size of the WHOLE filtered set, so the page can say what is left
 *  to load rather than stopping at an invisible ceiling (Anouk 2026-08-10: « tu sais pas remonter
 *  au-delà de 200 »). `stats` et `facets` ne viennent qu'avec la PREMIÈRE tranche (offset 0). */
export interface ActionLogPageMeta {
  total: number; limit: number; offset: number;
  /** Chiffres clés du jeu FILTRÉ entier, pas de la tranche chargée. */
  stats?: { users: number; addresses: number };
  facets?: {
    actions: ActionLogFacet[];
    users: ActionLogFacet[];
    states: { value: string; count: number }[];
    /** Combien de prises de verrou le périmètre contient — le « (N) » de la case « masquer le bruit ». */
    noise: number;
  };
}

/** Les filtres du journal. TOUS sont appliqués par le serveur : le total et « Charger plus » comptent le
 *  même jeu que la tranche affichée. Une valeur vide = pas de filtre. */
export interface ActionLogFilters {
  q: string;
  action: string;
  user: string;
  /** applied | reversed | partially_reversed */
  state: string;
  /** Jours AAAA-MM-JJ, bornes incluses, lus à l'heure de Bruxelles. */
  from: string;
  to: string;
  /** true = montrer aussi les prises de verrou. */
  noise: boolean;
}

export const EMPTY_ACTION_LOG_FILTERS: ActionLogFilters = {
  q: '', action: '', user: '', state: '', from: '', to: '', noise: false,
};

export function fetchActionLog(
  limit = 200, filters: ActionLogFilters = EMPTY_ACTION_LOG_FILTERS, offset = 0,
): Promise<{ data: ActionLogItem[]; meta?: ActionLogPageMeta }> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (filters.q.trim()) params.set('q', filters.q.trim());
  if (filters.action) params.set('action', filters.action);
  if (filters.user) params.set('user', filters.user);
  if (filters.state) params.set('state', filters.state);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.noise) params.set('noise', '1');
  return apiFetch<{ data: ActionLogItem[]; meta?: ActionLogPageMeta }>(`/admin/action-log?${params.toString()}`);
}

export function fetchActionLogDetail(id: string): Promise<{ data: ActionLogDetail }> {
  return apiFetch<{ data: ActionLogDetail }>(`/admin/action-log/${id}`);
}

export function reverseActionLog(id: string, note?: string | null): Promise<{ data: ReverseResult }> {
  return apiFetch<{ data: ReverseResult }>(`/admin/action-log/${id}/reverse`, {
    method: 'POST',
    body: JSON.stringify({ note: note ?? null }),
  });
}
